import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AnalyticsPurchaseTrackerService } from './services/analytics-purchase-tracker.service';
import { AnalyticsService } from './services/analytics.service';
import { SeoService } from './services/seo.service';
import { ViewportService } from './services/viewport.service';
import { isClassicGapHost } from './shared/brand';
import { scheduleGtmLoad } from './shared/gtm';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent
  implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  destroyed = new Subject<void>();
  isBrowser: boolean;
  private lastTrackedPath: string | null = null;

  public environment = environment;
  /** GAP vs WAR nav — from hostname when in browser. */
  classicGap = environment.classicGap;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() private gtmService: GoogleTagManagerService,
    private router: Router,
    @Optional() private store: Store | null,
    private viewportService: ViewportService,
    private analytics: AnalyticsService,
    private purchaseTracker: AnalyticsPurchaseTrackerService,
    private seo: SeoService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.classicGap = isClassicGapHost();
    }
  }

  ngOnInit(): void {
    this.viewportService.init();
    this.seo.start();
    if (this.isBrowser && environment.production) {
      this.addMarketingScripts();
    }

    this.initConsentDefaults();
    this.loadCookiebot();
    this.purchaseTracker.start();
    this.trackRouterAnalytics();

    this.router.events.pipe(takeUntil(this.destroyed)).subscribe((event) => {
      const policyCreatorState = (
        this.store?.snapshot() as
          | { policyCreatorState?: { sodanReplyHeads?: unknown } }
          | undefined
      )?.policyCreatorState;

      if (event['routerEvent']?.constructor.name === 'Q') {
        // GAP-470: /new-policy/1 has its own bootstrap skeleton — stay there on first visit.
        const newPolicyUrls = [
          '/new-policy/2',
          '/new-policy/3',
          '/new-policy/4',
          '/new-policy/5',
        ];

        if (
          event['routerEvent']?.url &&
          newPolicyUrls.includes(event['routerEvent'].url)
        ) {
          if (policyCreatorState && !policyCreatorState.sodanReplyHeads) {
            this.router.navigateByUrl('/apk/main');
          }
        }
      }
    });
  }

  ngAfterViewInit(): void {
    if (
      this.isBrowser &&
      (window as { Cypress?: unknown }).Cypress &&
      this.store
    ) {
      (window as { Cypress?: { ngStore?: Store } }).Cypress = {
        ...(window as { Cypress?: { ngStore?: Store } }).Cypress,
        ngStore: this.store,
      };
    }

    this.loadGtm();
  }

  ngAfterViewChecked(): void {
    this.setAppComponentPaddingTop();
  }

  private trackRouterAnalytics(): void {
    if (!this.analytics.isEnabled()) {
      return;
    }

    this.router.events
      .pipe(
        takeUntil(this.destroyed),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      )
      .subscribe((event) => {
        const path = event.urlAfterRedirects.split('?')[0];
        if (path === this.lastTrackedPath) {
          return;
        }
        this.lastTrackedPath = path;
        this.analytics.pageView(path, document.title);
        this.trackFunnelStep(path);
      });
  }

  private trackFunnelStep(path: string): void {
    const policyMatch = path.match(/^\/new-policy\/(\d+)/);
    if (policyMatch) {
      const step = Number(policyMatch[1]);
      if (step === 1) {
        this.analytics.beginCheckout('policy', path);
      }
      if (step >= 1 && step <= 5) {
        this.analytics.funnelStep({ step, path, flow: 'policy' });
      }
      return;
    }

    const warrantyMatch = path.match(/^\/new-warranty\/(\d+)/);
    if (warrantyMatch) {
      const step = Number(warrantyMatch[1]);
      if (step === 1) {
        this.analytics.beginCheckout('warranty', path);
      }
      if (step >= 1 && step <= 5) {
        this.analytics.funnelStep({ step, path, flow: 'warranty' });
      }
    }
  }

  /**
   * Consent Mode v2 defaults only when Cookiebot can later call consent update.
   * Without a CMP, denied defaults leave analytics_storage denied forever and
   * GA4 Realtime stays empty (hits go out as gcs=G100 / cookieless).
   */
  private initConsentDefaults(): void {
    if (!this.isBrowser || !environment.COOKIEBOT_ID) {
      return;
    }
    // Cookiebot may inject its script before AppComponent runs.
    if (document.getElementById('Cookiebot')) {
      return;
    }
    window.dataLayer = window.dataLayer || [];
    const gtag =
      window.gtag ??
      function gtagFallback(...args: unknown[]) {
        window.dataLayer!.push(args);
      };
    gtag('consent', 'default', {
      ad_personalization: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500,
    });
    gtag('set', 'ads_data_redaction', true);
  }

  private loadCookiebot(): void {
    if (!this.isBrowser || !environment.COOKIEBOT_ID) {
      return;
    }
    if (document.getElementById('Cookiebot')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'Cookiebot';
    script.src = 'https://consent.cookiebot.com/uc.js';
    script.type = 'text/javascript';
    script.setAttribute('data-cbid', environment.COOKIEBOT_ID);
    // Auto blocking replays XHR listeners with dispatchEvent. TrustedXhrFactory
    // ignores that replay while status is still 0, and keeps it once the real
    // HTTP status is available.
    script.setAttribute('data-blockingmode', 'auto');
    document.head.insertBefore(script, document.head.firstChild);
  }

  private loadGtm(): void {
    if (!this.isBrowser) {
      return;
    }
    scheduleGtmLoad(this.gtmService);
  }

  setAppComponentPaddingTop(): void {
    if (!this.isBrowser) {
      return;
    }
    const navBarWrapper = document.querySelector('.nav-bar__wrapper');
    const appComponent = document.querySelector('.appComponent') as HTMLElement;
    if (navBarWrapper && appComponent) {
      const navBarWrapperHeight = (navBarWrapper as HTMLElement).offsetHeight;
      appComponent.style.paddingTop = `${navBarWrapperHeight}px`;
    }
  }

  addMarketingScripts() {
    const fbRoot = document.createElement('div');
    fbRoot.id = 'fb-root';
    document.body.appendChild(fbRoot);

    const fbScript = document.createElement('script');
    fbScript.innerHTML = `window.fbAsyncInit = function () {
      FB.init({
        xfbml: true,
        version: 'v9.0',
      });
    };

    (function (d, s, id) {
      var js,
        fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s);
      js.id = id;
      js.src = 'https://connect.facebook.net/pl_PL/sdk/xfbml.customerchat.js';
      fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');`;
    document.body.appendChild(fbScript);

    const chatPluginCode = document.createElement('div');
    chatPluginCode.className = 'fb-customerchat';
    chatPluginCode.setAttribute('attribution', 'setup_tool');
    chatPluginCode.setAttribute('page_id', '103675818155120');
    chatPluginCode.setAttribute(
      'logged_in_greeting',
      'Dziękujemy za kontakt! Jakie informacje o GAP chciałbys się dowiedzieć?',
    );
    chatPluginCode.setAttribute(
      'logged_out_greeting',
      'Dziękujemy za kontakt!',
    );
    document.body.appendChild(chatPluginCode);
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
